import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskLine, Reveal, FadeIn } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CtaBand } from "../components/CtaBand";
import { IMAGES, SERVICES, CIRCUITS, MARQUEE_ITEMS, API_URL } from "../data/site";

const STATS = [
  { value: "30+", label: "Years in motorsport operations" },
  { value: "5", label: "Turnkey service lines" },
  { value: "Coast to coast", label: "Circuits across North America" },
  { value: "1", label: "Point of contact for everything" },
];

const CHAPTERS = [
  {
    num: "01",
    title: "You drive. That's the whole job.",
    text: "Amateur and semi-professional drivers lose weekends to logistics, prep, and paddock chaos. ABS LLC removes all of it — your only responsibility is the steering wheel.",
  },
  {
    num: "02",
    title: "Turnkey means turnkey.",
    text: "One call covers the program: car, transport, crew, tires, fuel, data, and hospitality details. Positive, reliable, and finished before you arrive.",
  },
  {
    num: "03",
    title: "Performance is a system.",
    text: "Physical training, simulator work, mental preparation, strategy, and analysis — integrated into one program that makes every weekend faster than the last.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const [latestRecap, setLatestRecap] = useState(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    axios
      .get(`${API_URL}/recaps`)
      .then((res) => setLatestRecap(res.data[0] || null))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#111111]">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img
            src={IMAGES.heroTrack}
            alt="Race circuit curving past grandstands at speed"
            className="h-full w-full object-cover opacity-60"
            style={{ filter: "grayscale(0.25) contrast(1.1)" }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/55 to-[#111111]/30" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(60% 50% at 25% 70%, rgba(230,57,70,0.14), transparent 70%)" }}
        />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-40 md:px-8 md:pb-20">
          <FadeIn delay={0.1}>
            <p className="font-overline flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-300 md:text-[11px]">
              <span className="inline-block h-px w-10 bg-[#e63946]" />
              Motorsport Concierge — North America
            </p>
          </FadeIn>

          <h1 className="font-display mt-7 text-[clamp(3rem,9vw,7.5rem)] font-extrabold leading-[0.95] tracking-tight text-white">
            <MaskLine delay={0.25}>You drive.</MaskLine>
            <MaskLine delay={0.38}>We handle</MaskLine>
            <MaskLine delay={0.51}>
              everything<span className="text-[#e63946]"> else.</span>
            </MaskLine>
          </h1>

          <FadeIn delay={0.85} className="mt-9 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <p className="max-w-md text-base leading-relaxed text-neutral-300 md:text-lg">
              Turnkey race programs, driver development, logistics, and trackside
              operations for amateur and semi-professional drivers — run with
              thirty years of professional discipline.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-inquiry-button"
                className="group inline-flex items-center gap-3 bg-[#e63946] px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-white hover:text-[#111111]"
              >
                Start an Inquiry
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-button"
                className="group inline-flex items-center gap-3 border border-white/40 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
              >
                Explore Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <section data-testid="home-proof" className="bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-neutral-200 px-0 md:grid-cols-4">
          {STATS.map((stat, i) => (
            <div key={i} className="bg-white px-6 py-12 md:px-10 md:py-16">
              <Reveal delay={i * 0.08}>
                <p className="font-display text-3xl font-extrabold tracking-tight text-[#111111] md:text-4xl">
                  {stat.value}
                </p>
                <p className="mt-3 text-sm leading-snug text-neutral-500">{stat.label}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="home-manifesto" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-32">
          <Reveal>
            <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
              The ABS Standard
            </p>
          </Reveal>
          <div className="mt-10 space-y-0">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.num} delay={0.05 * i}>
                <div className="grid gap-4 border-t border-neutral-200 py-10 md:grid-cols-12 md:gap-8 md:py-14">
                  <p className="font-overline text-sm tracking-[0.2em] text-neutral-400 md:col-span-2">{ch.num}</p>
                  <h2 className="font-display text-2xl font-bold tracking-tight text-[#111111] md:col-span-5 md:text-4xl">
                    {ch.title}
                  </h2>
                  <p className="text-base leading-relaxed text-neutral-600 md:col-span-5">{ch.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-services" className="bg-[#f8f9fa]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-32">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <Reveal>
              <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                What we run for you
              </p>
              <h2 className="font-display mt-5 text-4xl font-extrabold tracking-tight text-[#111111] sm:text-5xl">
                Five service lines.
                <br />
                One accountable team.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="group inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#111111] transition-colors duration-300 hover:text-[#e63946]"
              >
                All services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-px bg-neutral-200 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.num} delay={0.06 * i} className="h-full">
                <Link
                  to="/services"
                  data-testid={`home-service-card-${s.num}`}
                  className="group flex h-full flex-col justify-between bg-white p-8 transition-colors duration-300 hover:bg-[#111111] md:p-10"
                >
                  <div>
                    <p className="font-overline text-[11px] tracking-[0.25em] text-neutral-400 transition-colors duration-300 group-hover:text-neutral-500">
                      {s.num}
                    </p>
                    <h3 className="font-display mt-4 text-xl font-bold tracking-tight text-[#111111] transition-colors duration-300 group-hover:text-white md:text-2xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-600 transition-colors duration-300 group-hover:text-neutral-400">
                      {s.short}
                    </p>
                  </div>
                  <ArrowUpRight className="mt-8 h-5 w-5 text-neutral-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#e63946]" />
                </Link>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="h-full">
              <div className="img-treatment relative h-full min-h-[260px] overflow-hidden">
                <img
                  src={IMAGES.teamCar649}
                  alt="The #649 race car with the full crew after a weekend"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <p className="font-overline absolute bottom-6 left-6 text-[10px] uppercase tracking-[0.3em] text-white">
                  Performance, delivered
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="home-operations" className="grain relative bg-[#161616] text-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:grid-cols-12 md:px-8 md:py-32">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                Proof of operations
              </p>
              <h2 className="font-display mt-5 text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl">
                Active where
                <br />
                America races.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-neutral-400">
                ABS LLC supports client programs at the country's great road
                courses — paddock operations, logistics, and driver development
                delivered on real race weekends, not promises.
              </p>
              <Link
                to="/events-operations"
                data-testid="home-operations-link"
                className="group mt-8 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:text-[#e63946]"
              >
                Events & operations
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            {CIRCUITS.map((c, i) => (
              <Reveal key={c.name} delay={0.06 * i}>
                <div
                  data-testid={`home-circuit-${i}`}
                  className="group flex items-baseline justify-between gap-6 border-t border-neutral-800 py-5 transition-colors duration-300 hover:border-[#e63946]"
                >
                  <p className="font-display text-xl font-bold tracking-tight text-white md:text-2xl">{c.name}</p>
                  <p className="shrink-0 text-xs uppercase tracking-[0.18em] text-neutral-500">{c.region}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <div className="clip-corner img-treatment mt-8 overflow-hidden">
                <img
                  src={IMAGES.teamCanopy}
                  alt="ABS LLC team running trackside operations under the canopy"
                  className="aspect-[16/9] w-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {latestRecap && (
        <section data-testid="home-recap-teaser" className="border-t border-neutral-200 bg-white">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 md:grid-cols-12 md:px-8 md:py-28">
            {latestRecap.images?.length > 0 && (
              <div className="md:col-span-6">
                <Reveal>
                  <div className="clip-corner img-treatment overflow-hidden">
                    <img
                      src={latestRecap.images[0]}
                      alt={latestRecap.title}
                      className="aspect-[16/10] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </Reveal>
              </div>
            )}
            <div className={latestRecap.images?.length > 0 ? "md:col-span-6" : "md:col-span-12"}>
              <Reveal delay={0.1}>
                <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                  Latest race recap
                </p>
                <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-4xl lg:text-5xl">
                  {latestRecap.title}
                </h2>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="bg-[#f8f9fa] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600">
                    {latestRecap.circuit}
                  </span>
                  <span className="bg-[#f8f9fa] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600">
                    {latestRecap.date_label}
                  </span>
                  {latestRecap.result && (
                    <span className="bg-[#111111] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white">
                      {latestRecap.result}
                    </span>
                  )}
                </div>
                <Link
                  to="/recaps"
                  data-testid="home-recap-link"
                  className="group mt-8 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#111111] transition-colors duration-300 hover:text-[#e63946]"
                >
                  All race recaps
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <CtaBand />
    </div>
  );
}
